/**
 * 命令的原子单位 —— Patch。
 *
 * 两条铁律（决定 undo 是否可靠）：
 *  1) 属性改动 → 细粒度 attr patch（省内存、回放精确）
 *  2) 结构改动（增/删/移动节点、编组解组）→ **父容器快照**（before/after 各一份）
 *     「插入点 + 索引」式的结构 patch 在 undo 顺序、兄弟引用、注释/文本节点上极易出错；
 *     快照朴素但绝对正确，代价靠 History 的内存预算兜住。
 *
 * 所有构造器在「没有实际变化」时返回 null —— 空操作绝不能进历史栈，
 * 否则「打开→不改→导出」就不再是字节相同（M0 硬指标）。
 */
import { SVG_NS, nodeAt, pathOf, type Path } from './dom';

export type Patch =
  | { k: 'attr'; path: Path; name: string; before: string | null; after: string | null }
  | { k: 'container'; parent: Path; before: string; after: string }
  /**
   * 闭包型：apply 才真正改 DOM（结构类 op 用）。
   * 为什么不用 container 快照直接承载结构改动：
   *   ① 让"结构 op 零副作用"成立 —— 快照模式的 after 必须在构造时就由实况产生，
   *      那就意味着构造即改 DOM（违反护栏）；
   *   ② apply 走真实 DOM 操作（appendChild 等）能**保持元素身份**，
   *      选中集/列表持有的引用不会失效；revert 才退回快照（撤销时重建节点可接受）。
   */
  | { k: 'custom'; label: string; bytes: number; apply(): void; revert(): void };;

export const attrPatch = (
  path: Path,
  name: string,
  before: string | null,
  after: string | null,
): Patch | null => (before === after ? null : { k: 'attr', path, name, before, after });

/** 序列化父节点的全部子节点（含文本与注释）—— 结构改动的快照格式 */
export const snapshotOf = (parent: Element): string =>
  Array.from(parent.childNodes, (n) => new XMLSerializer().serializeToString(n)).join('');

/** 结构改动的收尾：把「改之前」的快照与本刻的实况配对；无变化返回 null */
export function containerPatch(root: Element, parent: Element, before: string): Patch | null {
  const after = snapshotOf(parent);
  if (before === after) return null;
  const path = pathOf(parent, root);
  if (!path) throw new Error('容器不在文档树内，无法快照');
  return { k: 'container', parent: path, before, after };
}

const NS_XLINK = 'http://www.w3.org/1999/xlink';

/** 用快照字符串整体替换父节点的子节点（保留命名空间、注释、文本） */
function setChildren(parent: Element, html: string): void {
  const holder = new DOMParser().parseFromString(
    `<svg xmlns="${SVG_NS}" xmlns:xlink="${NS_XLINK}">${html}</svg>`,
    'image/svg+xml',
  ).documentElement;
  if (holder.tagName.toLowerCase() === 'parsererror') throw new Error('容器快照解析失败');
  while (parent.firstChild) parent.removeChild(parent.firstChild);
  const doc = parent.ownerDocument;
  for (let n = holder.firstChild; n; n = n.nextSibling) {
    parent.appendChild(doc.importNode(n, true));
  }
}

/**
 * 通用闭包 patch：apply/revert 都自己给（整树替换、源码应用这类"说不清是哪种细粒度改动"的场景用）。
 * 约定：apply 必须把文档带进目标态、revert 必须精确回到 apply 前的状态。
 */
export const makeCustomPatch = (
  label: string,
  apply: () => void,
  revert: () => void,
  bytes = 0,
): Patch => ({ k: 'custom', label, bytes, apply, revert });

/**
 * 结构改动的闭包 patch（编组/解组/层序/复制/粘贴共用）：
 * apply 走真实 DOM 操作以保持元素身份；revert 退回快照。
 */
export const makeStructuralPatch = (
  parent: Element,
  before: string,
  label: string,
  apply: () => void,
): Patch => ({
  k: 'custom',
  label,
  bytes: before.length * 2,
  apply,
  revert: () => {
    if (snapshotOf(parent) !== before) setChildren(parent, before);
  },
});

/** 用快照字符串整体替换父节点的子节点（structural revert 也走它） */
export function setInnerXml(parent: Element, html: string): void {
  setChildren(parent, html);
}

const target = (root: Element, p: Exclude<Patch, { k: 'custom' }>): Element => {
  const el = p.k === 'attr' ? nodeAt(root, p.path) : nodeAt(root, p.parent);
  if (!el) throw new Error(`patch 目标不存在：${p.k === 'attr' ? p.path.join('.') : p.parent.join('.')}`);
  return el;
};

const targetOf = (root: Element, p: Exclude<Patch, { k: 'custom' }>): Element => target(root, p);

const setAttr = (el: Element, name: string, value: string | null): void => {
  if (value === null) el.removeAttribute(name);
  else el.setAttribute(name, value);
};

/**
 * container patch 必须幂等：结构类 op 在构造 patch 时已经改过 DOM，
 * execute/revert 再"设置"一次会把整棵子树重建 —— 选中集与列表持有的元素引用随即变成游离节点。
 * 所以先比目标状态，已达成则不动 DOM（元素身份得以保持）。
 */
export function applyPatch(root: Element, p: Patch): void {
  if (p.k === 'custom') {
    p.apply();
    return;
  }
  const el = targetOf(root, p);
  if (p.k === 'attr') setAttr(el, p.name, p.after);
  else if (snapshotOf(el) !== p.after) setChildren(el, p.after);
}

export function revertPatch(root: Element, p: Patch): void {
  if (p.k === 'custom') {
    p.revert();
    return;
  }
  const el = targetOf(root, p);
  if (p.k === 'attr') setAttr(el, p.name, p.before);
  else if (snapshotOf(el) !== p.before) setChildren(el, p.before);
}

/** 内存估算（供 History 预算淘汰） */
export const patchBytes = (p: Patch): number => {
  if (p.k === 'custom') return p.bytes;
  return p.k === 'attr'
    ? p.name.length + (p.before?.length ?? 0) + (p.after?.length ?? 0)
    : p.before.length + p.after.length;
};

/** 两条 patch 是否作用于同一处（合并判断用） */
export const sameTarget = (a: Patch, b: Patch): boolean => {
  if (a.k !== b.k || a.k === 'custom' || b.k === 'custom') return false;
  if (a.k === 'attr' && b.k === 'attr') return a.name === b.name && a.path.join('.') === b.path.join('.');
  if (a.k === 'container' && b.k === 'container') return a.parent.join('.') === b.parent.join('.');
  return false;
};
